import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import EmailService from "services/EmailService";
import Utils from "utils";
import openNotification from "utils/notification";

export const EMAIL_ERROR = {
  CONFIG_NOT_FOUND: 2200,
  SENDER_NOT_FOUND: 2201,
  SUPPLIER_EMAIL_REQUIRED: 2209,
  PURCHASE_ORDER_NOT_FOUND: 2210,
  SEND_FAILED: 2211,
  PURCHASE_ORDER_ALREADY_SENT: 2213,
};

const getReturnCode = (response) => response?.data?.data ?? null;

const notifySuccess = () =>
  openNotification(
    "success",
    Utils.setLocale("admin.settings.popup.notification.success"),
    Utils.setLocale("admin.settings.popup.notification.title"),
  );

const notifyError = (description) =>
  openNotification(
    "error",
    description || Utils.setLocale("admin.settings.popup.notification.error"),
    Utils.setLocale("admin.settings.popup.notification.title"),
  );

export const sendPurchaseOrderEmailApi = createAsyncThunk(
  "email/sendPurchaseOrderEmailApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await EmailService.sendPurchaseOrderEmail(payload);

      if (onSuccess) onSuccess(response);

      const returnCode = getReturnCode(response);
      const errorCode = returnCode?.errorCode;

      if (!errorCode) {
        notifySuccess();
      } else if (errorCode !== EMAIL_ERROR.PURCHASE_ORDER_ALREADY_SENT) {
        notifyError(returnCode?.description);
      }

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const resendEmailRecipientApi = createAsyncThunk(
  "email/resendEmailRecipientApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, recipientId } = data;

      const response = await EmailService.resendEmailRecipient(recipientId);

      if (onSuccess) onSuccess(response);

      const returnCode = getReturnCode(response);

      if (returnCode?.errorCode) {
        notifyError(returnCode.description);
      }

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const verifyBrevoCredentialApi = createAsyncThunk(
  "email/verifyBrevoCredentialApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, apiCredentialId } = data;

      const response = await EmailService.verifyCredential(apiCredentialId);

      if (onSuccess) onSuccess(response);

      const returnCode = getReturnCode(response);

      if (returnCode?.errorCode) {
        notifyError(returnCode.description);
      } else {
        notifySuccess();
      }

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getEmailHistoryApi = createAsyncThunk(
  "email/getEmailHistoryApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await EmailService.getEmailHistory(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getEmailStatusSummaryApi = createAsyncThunk(
  "email/getEmailStatusSummaryApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await EmailService.getEmailStatusSummary(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getEmailDetailApi = createAsyncThunk(
  "email/getEmailDetailApi",
  async (id, { rejectWithValue }) => {
    try {
      const response = await EmailService.getEmailDetail(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const createEmailCampaignApi = createAsyncThunk(
  "email/createEmailCampaignApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await EmailService.createEmailCampaign(payload);

      if (onSuccess) onSuccess(response);

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const updateEmailCampaignApi = createAsyncThunk(
  "email/updateEmailCampaignApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await EmailService.updateEmailCampaign(payload);

      if (onSuccess) onSuccess(response);

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

const initialState = {
  loading: false,
  loadingSend: false,
  loadingDetail: false,
  loadingSummary: false,
  loadingVerify: false,

  emailHistoryList: [],
  statusSummary: [],
  emailDetail: null,

  totalCount: 0,

  currentRequestId: undefined,
};

export const emailSlice = createSlice({
  name: "email",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    setEmailDetail: (state, action) => {
      state.emailDetail = action.payload || null;
    },
    clearEmailDetail: (state) => {
      state.emailDetail = null;
      state.loadingDetail = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== gửi PO cho NCC =====
      .addCase(sendPurchaseOrderEmailApi.pending, (state) => {
        state.loadingSend = true;
      })
      .addCase(sendPurchaseOrderEmailApi.fulfilled, (state) => {
        state.loadingSend = false;
      })
      .addCase(sendPurchaseOrderEmailApi.rejected, (state) => {
        state.loadingSend = false;
      })

      // ===== gửi lại 1 dòng =====
      .addCase(resendEmailRecipientApi.pending, (state) => {
        state.loadingSend = true;
      })
      .addCase(resendEmailRecipientApi.fulfilled, (state) => {
        state.loadingSend = false;
      })
      .addCase(resendEmailRecipientApi.rejected, (state) => {
        state.loadingSend = false;
      })

      .addCase(verifyBrevoCredentialApi.pending, (state) => {
        state.loadingVerify = true;
      })
      .addCase(verifyBrevoCredentialApi.fulfilled, (state) => {
        state.loadingVerify = false;
      })
      .addCase(verifyBrevoCredentialApi.rejected, (state) => {
        state.loadingVerify = false;
      })

      // ===== lịch sử gửi =====
      .addCase(getEmailHistoryApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(getEmailHistoryApi.fulfilled, (state, action) => {
        const paging = action.payload?.data ?? {};
        state.loading = false;
        state.emailHistoryList = paging.data ?? [];
        state.totalCount = paging.totalCount ?? 0;
      })
      .addCase(getEmailHistoryApi.rejected, (state) => {
        state.loading = false;
      })

      // ===== thống kê trạng thái =====
      .addCase(getEmailStatusSummaryApi.pending, (state) => {
        state.loadingSummary = true;
      })
      .addCase(getEmailStatusSummaryApi.fulfilled, (state, action) => {
        state.loadingSummary = false;
        state.statusSummary = action.payload?.data ?? [];
      })
      .addCase(getEmailStatusSummaryApi.rejected, (state) => {
        state.loadingSummary = false;
        state.statusSummary = [];
      })

      // ===== chi tiết =====
      .addCase(getEmailDetailApi.pending, (state) => {
        state.loadingDetail = true;
      })
      .addCase(getEmailDetailApi.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.emailDetail = action.payload?.data ?? null;
      })
      .addCase(getEmailDetailApi.rejected, (state) => {
        state.loadingDetail = false;
        state.emailDetail = null;
      })

      // ===== campaign =====
      .addCase(createEmailCampaignApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(createEmailCampaignApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createEmailCampaignApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(updateEmailCampaignApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEmailCampaignApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateEmailCampaignApi.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { showLoading, setEmailDetail, clearEmailDetail } =
  emailSlice.actions;

export default emailSlice.reducer;